
import React from 'react';
import { Button } from '@/components/ui/button';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { Linkedin, Calendar, ChevronRight, PhoneCall } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';

const Hero = () => {
  const isMobile = useIsMobile();
  const phoneNumberFormatted = "+18609825635";
  
  // Added new scroll function for the button
  const handleScrollToContact = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const contactSection = document.querySelector('#contact');
    
    if (contactSection) {
      // Calculate proper offset for fixed header
      const headerOffset = isMobile ? 90 : 120;
      const elementPosition = contactSection.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };
  
  return <section className="relative min-h-[90vh] md:min-h-screen flex items-center overflow-hidden pt-16 md:pt-20">
      {/* Background image with overlay */}
      <div className="absolute inset-0 z-0">
        <img src="/lovable-uploads/00a86d7f-483a-4af5-8331-f041431c1e2b.png" alt="Mountain view" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-maroon/70 to-maroon/40"></div>
      </div>
      
      <div className="container relative z-10 px-4 sm:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
          <div className="space-y-5 md:space-y-6 bg-white/90 backdrop-blur-sm p-6 md:p-8 rounded-tr-[30px] md:rounded-tr-[40px] rounded-bl-[30px] md:rounded-bl-[40px] shadow-lg">
            {/* Added company name as a heading */}
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-heading font-semibold text-maroon mb-2">KTJones Management Consulting</h1>
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-semibold leading-tight text-charcoal">
              Transforming challenges into <span className="text-maroon">opportunity</span>, together
            </h2>
            <h3 className="text-xl md:text-2xl lg:text-3xl font-heading text-charcoal-light">
              I'm with you every step.
            </h3>
            <p className="text-base md:text-lg text-charcoal-light max-w-lg">
              Stronger systems. Smoother operations. Scalable growth. 
              Let me help your business reach new heights with strategic operational excellence.
            </p>
            <div className="pt-2 md:pt-4 flex">
              <Button className="bg-maroon hover:bg-maroon-light text-white font-medium text-base md:text-lg px-4 md:px-6 py-3 md:py-6 h-auto rounded-md max-w-full flex-shrink" onClick={handleScrollToContact}>
                <span className="truncate">{isMobile ? "Book a Free Consultation" : "Book a Free Strategy Session"}</span> <ChevronRight className="ml-1 md:ml-2 flex-shrink-0" size={isMobile ? 18 : 20} />
              </Button>
            </div>
          </div>
          <div className="hidden lg:block">
            {/* This column is intentionally left empty since we're using a full background image */}
          </div>
        </div>
      </div>

      {/* Floating Action Buttons - Left Side */}
      {isMobile ? <>
          <div className="fixed bottom-6 left-4 flex flex-col gap-3 z-50">
            <Button className="rounded-full shadow-lg bg-maroon hover:bg-maroon-light text-white p-4 w-14 h-14 flex items-center justify-center" onClick={() => window.open('https://www.linkedin.com/in/kristen-tangarone/', '_blank')} aria-label="Connect on LinkedIn">
              <Linkedin size={24} />
            </Button>
            <Button className="rounded-full shadow-lg bg-white border-2 border-maroon hover:bg-maroon/5 text-maroon p-4 w-14 h-14 flex items-center justify-center" onClick={handleScrollToContact} aria-label="Schedule a consultation">
              <Calendar size={24} />
            </Button>
          </div>
          
          {/* Phone Call Button - Right Side */}
          <div className="fixed bottom-6 right-4 z-50">
            <Button className="rounded-full shadow-lg bg-maroon hover:bg-maroon-light text-white p-4 w-14 h-14 flex items-center justify-center" onClick={() => window.location.href = `tel:${phoneNumberFormatted}`} aria-label="Call Kristen">
              <PhoneCall size={24} />
            </Button>
          </div>
        </> : <div className="fixed bottom-10 right-10 flex flex-col gap-4 z-50">
          <Button className="rounded-full shadow-lg bg-maroon hover:bg-maroon-light text-white p-4 w-14 h-14 flex items-center justify-center" onClick={() => window.open('https://www.linkedin.com/in/kristen-tangarone/', '_blank')} aria-label="Connect on LinkedIn">
            <Linkedin size={24} />
          </Button>
          <Button className="rounded-full shadow-lg bg-white border-2 border-maroon hover:bg-maroon/5 text-maroon p-4 w-14 h-14 flex items-center justify-center" onClick={handleScrollToContact} aria-label="Schedule a consultation">
            <Calendar size={24} />
          </Button>
        </div>}

      {/* Abstract curve decoration */}
      <div className="absolute bottom-0 left-0 w-full h-16 md:h-20 bg-white" style={{
      clipPath: 'polygon(0 100%, 100% 100%, 100% 0)'
    }}></div>
    </section>;
};
export default Hero;
